import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * 클래스명을 병합하는 유틸리티 함수
 * - clsx로 조건부 클래스명 처리
 * - tailwind-merge로 중복 스타일 충돌 해결
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 파일을 Base64 문자열로 변환
 * @param file 변환할 파일
 * @returns Promise<string> Base64 문자열
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

/**
 * UUID 생성 함수
 * @returns UUID 문자열
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// 이미지 캐시 저장소
const imageCache: Record<string, HTMLImageElement> = {};

/**
 * 이미지 URL을 로드하여 Canvas에 적용 (캐싱 적용)
 * @param url 이미지 URL (또는 Base64 문자열)
 * @returns Promise<HTMLImageElement>
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  // 캐시에 이미지가 있는 경우 캐시된 이미지 반환
  if (imageCache[url] && imageCache[url].complete) {
    return Promise.resolve(imageCache[url]);
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // 로컬 서버 또는 Data URL인 경우 crossOrigin 설정 불필요
    if (!url.startsWith('data:') && !url.startsWith('blob:') && !url.includes('localhost')) {
      img.crossOrigin = 'anonymous';
    }
    
    
    // 이미지 로드 전에 초기값 설정으로 깜빡임 방지
    img.style.opacity = '0';
    
    img.onload = () => {
      // 이미지 캐시에 저장
      imageCache[url] = img;
      // 완전히 로드된 상태에서 반환
      img.style.opacity = '1';
      resolve(img);
    };
    
    img.onerror = (e) => {
      console.error(`이미지 로드 실패: ${url.substring(0, 30)}...`, e);
      reject(new Error('이미지를 로드할 수 없습니다.'));
    };
    
    // 이미지 로드 시작
    img.src = url;
    
    // 브라우저 캐시에서 이미 로드된 이미지인 경우
    if (img.complete) {
      imageCache[url] = img;
      img.style.opacity = '1';
      resolve(img);
    }
  });
}

/**
 * 이미지를 미리 로드하여 캐시에 저장
 * @param urls 이미지 URL 배열
 * @returns Promise<void>
 */
export function preloadImages(urls: string[]): Promise<void> {
  const promises = urls.map(url => {
    return loadImage(url).catch(err => {
      console.warn(`이미지 미리 로드 실패: ${url}`, err);
      return null;
    });
  });
  
  return Promise.all(promises).then(() => {
  });
}

/**
 * 두 값 중 작은 값을 반환하는 유틸리티
 */
export const min = Math.min;

/**
 * 두 값 중 큰 값을 반환하는 유틸리티
 */
export const max = Math.max;

/**
 * 값을 최소-최대 범위 내로 제한하는 유틸리티
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * 지연 함수
 * @param ms 지연 시간 (밀리초)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 이미지에서 주요 색상(가장 많이 사용된 색상)을 추출합니다.
 * @param imageUrl 이미지 URL(base64 문자열 포함)
 * @returns HEX 색상 코드(#RRGGBB 형식)
 */
export async function extractDominantColor(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      img.onload = () => {
        // 캔버스 생성
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          reject(new Error('캔버스 컨텍스트를 생성할 수 없습니다.'));
          return;
        }
        
        // 이미지 크기 조절 (성능 최적화)
        const size = 50; // 작은 크기로 리사이징
        canvas.width = size;
        canvas.height = size;
        
        // 캔버스에 이미지 그리기
        ctx.drawImage(img, 0, 0, size, size);
        
        // 픽셀 데이터 가져오기
        const imageData = ctx.getImageData(0, 0, size, size).data;
        
        // 색상 카운팅을 위한 객체
        const colorCounts: Record<string, number> = {};
        
        // 각 픽셀의 색상 카운팅
        for (let i = 0; i < imageData.length; i += 4) {
          const r = imageData[i];
          const g = imageData[i + 1];
          const b = imageData[i + 2];
          const a = imageData[i + 3];
          
          // 투명 픽셀 무시
          if (a < 128) continue;
          
          // 색상값을 단순화 (더 효율적인 계산을 위해)
          const simplifiedR = Math.round(r / 10) * 10;
          const simplifiedG = Math.round(g / 10) * 10;
          const simplifiedB = Math.round(b / 10) * 10;
          
          const key = `${simplifiedR}-${simplifiedG}-${simplifiedB}`;
          colorCounts[key] = (colorCounts[key] || 0) + 1;
        }
        
        // 가장 많이 등장한 색상 찾기
        let dominantColor = '';
        let maxCount = 0;
        
        for (const key in colorCounts) {
          if (colorCounts[key] > maxCount) {
            maxCount = colorCounts[key];
            dominantColor = key;
          }
        }
        
        // 색상이 추출되지 않은 경우
        if (!dominantColor) {
          resolve('#ffffff'); // 기본 하얀색 반환
          return;
        }
        
        // RGB 값으로 분리
        const [r, g, b] = dominantColor.split('-').map(Number);
        
        // HEX 색상으로 변환
        const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        resolve(hex);
      };
      
      img.onerror = () => {
        reject(new Error('이미지 로드에 실패했습니다.'));
      };
      
      img.src = imageUrl;
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 색상의 밝기를 판단하여 대비되는 텍스트 색상 반환
 * W3C 웹 접근성 가이드라인(WCAG)에 따른 상대 휘도 계산 방식 사용
 * - 어두운 배경에는 흰색 텍스트
 * - 밝은 배경에는 검은색 텍스트
 * 
 * @param backgroundColor 배경 색상 (HEX 문자열)
 * @returns 적절한 텍스트 색상 (HEX 문자열)
 */
export function getContrastTextColor(backgroundColor: string): string {
  // 기본값 설정
  if (!backgroundColor || backgroundColor === 'transparent') {
    return '#000000';
  }
  
  // HEX 색상 형식 정리 (#fff -> #ffffff)
  let hexColor = backgroundColor.replace('#', '');
  if (hexColor.length === 3) {
    hexColor = hexColor[0] + hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2];
  }
  
  // HEX를 RGB로 변환
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);
  
  // W3C 상대 휘도(relative luminance) 계산
  // 1. RGB 값을 0-1 범위로 정규화
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  // 2. 감마 보정 적용 (sRGB 색공간)
  const gammaCorrectedR = rNorm <= 0.03928 
    ? rNorm / 12.92 
    : Math.pow((rNorm + 0.055) / 1.055, 2.4);
  const gammaCorrectedG = gNorm <= 0.03928 
    ? gNorm / 12.92 
    : Math.pow((gNorm + 0.055) / 1.055, 2.4);
  const gammaCorrectedB = bNorm <= 0.03928 
    ? bNorm / 12.92 
    : Math.pow((bNorm + 0.055) / 1.055, 2.4);
  
  // 3. 가중 평균으로 상대 휘도 계산 (ITU-R BT.709 표준)
  const luminance = 0.2126 * gammaCorrectedR + 0.7152 * gammaCorrectedG + 0.0722 * gammaCorrectedB;
  
  // 4. 임계값 0.5 기준으로 어두움/밝음 판단 (0-1 범위)
  // 사용자 요청: 어두운 색상 → 흰색 텍스트, 밝은 색상 → 검은색 텍스트
  const textColor = luminance > 0.5 ? '#000000' : '#ffffff';
  
  // 디버깅 로그
  
  return textColor;
}

/**
 * 애니메이션 키프레임을 위한 CSS 정의
 * - Accordion 컴포넌트 애니메이션 등에 사용
 */
export const animationStyles = `
  @keyframes accordion-down {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes accordion-up {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  }

  .animate-accordion-down {
    animation: accordion-down 0.2s ease-out;
  }

  .animate-accordion-up {
    animation: accordion-up 0.2s ease-out;
  }
`; 